import { getBaseRole } from '@/helpers/customRoles';
import { FC, useEffect, useMemo, useState } from 'react'
import Modal from '@common/Modal'
import mclasses from '@pages/Admin/CRM/styles/modal.module.css'
import iclasses from '@pages/Admin/CRM/styles/input.module.css'
import classes from './index.module.css'
import { IcoRemove, InfoIcon } from '@/utils/icons'
import Button from '@UI/New/Button'
import { InputNew } from '@UI/InputNew'
import cn from 'classnames'
import { useAddAlert } from '@/hooks/useAddAlert'
import { directionGroupApi, usersAPI } from '@/api'
import { Checkbox, Form, Select, Switch } from 'antd'
import Preloader from '@UI/Preloader'
import { FormProps, useForm } from 'antd/es/form/Form'
import FormItem from 'antd/es/form/FormItem'
import { useAsyncFn, useDebounce } from 'react-use'
import useRoles from '@/hooks/useRoles'
import { useGetDirection } from '@/hooks/useGetDirection'
import { useGetId } from '@/hooks/useGetId'
import { useGetGroupName } from '@pages/Admin/PlatformSettings2/AddSettingsEmployeesPersonAccount/hooks/useGetGroupName'
import { InputWhite } from '@UI/New/InputWhite'
import TooltipWrapper from '@UI/New/TooltipWrapper/TooltipWrapper'
import { useGetAdditionalSettings } from '@pages/Admin/CourseSettings/components/AdditionalCourseSettings/hooks/useGetAdditionalSettings'
import { groupName } from '@pages/Admin/CourseSettings/components/AdditionalCourseSettings/utils/fetchAdditionalSettings'
import classNames from 'classnames'
import { useGetUserProfile } from '@/hooks/useGetUserProfile'
import rolesEnum from '@/constant/rolesEnum'
import useFranchises from '@/hooks/useFranchises'
import Text from '@UI/New/Text'
import {
  getTeacherSalaryTooltip,
  TEACHER_SALARY_OPTIONS,
} from '../../utils/teacherSalary'
import { TeacherSalaryType } from '../../types'
import BaseSelect from '@pages/Admin/CRM/components/base/Select/Select'

type DirectionSett =
  | {
      directionId: number
      enableCourseSelect?: boolean
    }
  | {
      directionId?: number | null
      enableCourseSelect: true
    }

type CreateGroupProps = DirectionSett & {
  open: boolean
  onClose: () => void
  onSuccess: (groupId: number) => void
  teacherId?: number | null
  defaultLessonDuration?: number
  studentLessonPrice?: number | null
  groupType?: IGroupType | null
  allowedFranchiseIds?: number[]
}

export interface FieldType {
  franchise_id: number | null
  direction_id: number
  teacher_id: number | null
  name: string
  max_students: number | null
  apply_max_students: boolean
  salary_type: TeacherSalaryType
  salary: number | null
  calculate_teacher_salary: boolean
  duration_minutes: number | null
  lesson_duration: number | null
  student_lesson_price: number | null
  is_lesson_payment_group: boolean
  is_shared_balance: boolean
  min_amount_enabled: boolean
  min_amount: number | null
  has_additional_teachers: boolean
  additional_teacher_ids: number[]
}

type CreateGroupContentProps = CreateGroupProps & {}

const DEFAULT_SALARY_TYPE: TeacherSalaryType = 'per-lesson'
const MIN_AMOUNT_TOOLTIP =
  'Включив данный чек-бокс, вы сможете внести минимальную сумму, которую получит педагог вне зависимости от количества учащихся, пришедших на урок'
const ADDITIONAL_TEACHERS_TOOLTIP =
  'Если вы включите данный тумблер, вы сможете добавить любого педагога в качестве дополнительного к этой группе. У дополнительного педагога будут появляться все те же домашние работы, контрольные и пробники, которые задал основной педагог. Дополнительные педагоги смогут проверять эти работы, а также задавать собственные работы в этой группе.'

const normalizeNonNegativeNumberInput = (value: string) => {
  if (!value) return ''
  return value.replace('-', '')
}

const CreateGroupModalContent: FC<CreateGroupContentProps> = ({
  directionId,
  enableCourseSelect,
  open,
  onClose,
  onSuccess,
  teacherId,
  defaultLessonDuration,
  studentLessonPrice,
  groupType,
  allowedFranchiseIds,
}) => {
  const { groupNamePlural, groupNameSingular } = useGetGroupName()
  const addAlert = useAddAlert()
  const [form] = useForm()
  const [submitting, setSubmitting] = useState(false)
  const {
    enabled: franchisesEnabled,
    options: franchiseOptions,
    autoSelect,
    loading: franchisesLoading,
  } = useFranchises()
  const availableFranchiseOptions = useMemo(() => {
    if (allowedFranchiseIds === undefined) return franchiseOptions

    const allowedIds = allowedFranchiseIds.map(Number)
    return franchiseOptions.filter((option) =>
      allowedIds.includes(Number(option.value)),
    )
  }, [allowedFranchiseIds, franchiseOptions])
  const defaultFranchiseId = useMemo(() => {
    if (
      autoSelect &&
      availableFranchiseOptions.some(
        (option) => Number(option.value) === Number(autoSelect),
      )
    ) {
      return Number(autoSelect)
    }

    return availableFranchiseOptions.length === 1
      ? Number(availableFranchiseOptions[0].value)
      : null
  }, [autoSelect, availableFranchiseOptions])
  const selectedFranchiseId = Form.useWatch('franchise_id', form)

  const { isSeminarian } = useRoles()
  const directionsState = useGetDirection(isSeminarian, {
    anySeminarian: false,
    skip:
      !open ||
      franchisesLoading ||
      (franchisesEnabled && !selectedFranchiseId),
    ...(selectedFranchiseId
      ? { franchise_ids: [selectedFranchiseId] }
      : {}),
  })

  const [searchName, setSearchName] = useState('')
  const [colleaguesState, getColleaguesState] = useAsyncFn(
    async (name?: string, franchiseId?: number | null) =>
      usersAPI.searchUsersForSelect({
        employeesOnly: 1,
        'per-page': '1000',
        ...(franchiseId ? { franchise_id: franchiseId } : {}),
        ...(name ? { name } : {}),
      }),
  )
  const [additionalTeachersState, getAdditionalTeachersState] = useAsyncFn(
    async (name?: string, franchiseId?: number | null) =>
      usersAPI.searchUsersForSelect({
        employeesOnly: 1,
        'per-page': '1000',
        ...(franchiseId ? { franchise_id: franchiseId } : {}),
        ...(name ? { name } : {}),
      }),
  )

  useEffect(() => {
    if (
      !open ||
      isSeminarian ||
      franchisesLoading ||
      (franchisesEnabled && !selectedFranchiseId)
    ) {
      return
    }

    getColleaguesState(undefined, selectedFranchiseId)
  }, [
    franchisesEnabled,
    franchisesLoading,
    getColleaguesState,
    isSeminarian,
    open,
    selectedFranchiseId,
  ])

  useEffect(() => {
    if (
      !open ||
      franchisesLoading ||
      (franchisesEnabled && !selectedFranchiseId)
    ) {
      return
    }

    getAdditionalTeachersState(undefined, selectedFranchiseId)
  }, [
    franchisesEnabled,
    franchisesLoading,
    getAdditionalTeachersState,
    open,
    selectedFranchiseId,
  ])

  useDebounce(
    () => {
      if (
        !open ||
        franchisesLoading ||
        (franchisesEnabled && !selectedFranchiseId)
      ) {
        return
      }

      if (!isSeminarian) {
        getColleaguesState(searchName || undefined, selectedFranchiseId)
      }
    },
    500,
    [
      searchName,
      open,
      franchisesEnabled,
      franchisesLoading,
      isSeminarian,
      selectedFranchiseId,
    ],
  )

  useEffect(() => {
    const currentValue = form.getFieldValue('franchise_id')
    if (
      currentValue &&
      availableFranchiseOptions.some(
        (option) => Number(option.value) === Number(currentValue),
      )
    ) {
      return
    }

    form.setFieldValue('franchise_id', defaultFranchiseId)
    form.setFieldValue('teacher_id', null)
    if (enableCourseSelect) {
      form.setFieldValue('direction_id', null)
    }
  }, [
    availableFranchiseOptions,
    defaultFranchiseId,
    enableCourseSelect,
    form,
  ])

  const seminarianId = useGetId()
  const allColleagues = colleaguesState.value?.data?.data ?? []
  const additionalTeacherOptions =
    additionalTeachersState.value?.data?.data ?? []

  useEffect(() => {
    if (!colleaguesState.error) return

    addAlert({
      title: 'Ошибка',
      text: 'Ошибка при получении списка педагогов',
      type: 'error',
    })
    onClose()
  }, [addAlert, colleaguesState.error, onClose])

  const directionIdFromForm = Form.useWatch('direction_id', form)
  const selectedTeacherId = Form.useWatch('teacher_id', form)
  const selectedDirectionId = Form.useWatch('direction_id', form)
  const applyMaxStudents = Form.useWatch('apply_max_students', form)
  const maxStudents = Form.useWatch('max_students', form)
  const name = Form.useWatch('name', form)
  const salaryType = Form.useWatch('salary_type', form) ?? DEFAULT_SALARY_TYPE
  const calculateTeacherSalary = Form.useWatch(
    'calculate_teacher_salary',
    form,
  )
  const salaryValue = Form.useWatch('salary', form)
  const durationMinutes = Form.useWatch('duration_minutes', form)
  const lessonDuration = Form.useWatch('lesson_duration', form)
  const studentLessonPriceValue = Form.useWatch('student_lesson_price', form)
  const isLessonPaymentGroupEnabled = Form.useWatch(
    'is_lesson_payment_group',
    form,
  )
  const isSharedBalance = Form.useWatch('is_shared_balance', form)
  const minAmountEnabled = Form.useWatch('min_amount_enabled', form)
  const minAmount = Form.useWatch('min_amount', form)
  const hasAdditionalTeachers = Form.useWatch(
    'has_additional_teachers',
    form,
  )

  const directionIdToSend = enableCourseSelect
    ? directionIdFromForm
    : directionId
  const isGroupTypeFixed = !!groupType
  const selectedDirectionHasLessonPaymentTariff = useMemo(
    () =>
      directionsState.tariffs?.some(
        (tariff: any) =>
          tariff.directionId === directionIdToSend &&
          tariff.type === 'group_per_lesson',
      ),
    [directionIdToSend, directionsState.tariffs],
  )
  const shouldShowLessonPaymentGroupSwitch =
    !isGroupTypeFixed && selectedDirectionHasLessonPaymentTariff
  const isLessonPaymentGroup =
    groupType === 'lesson-payment' ||
    (shouldShowLessonPaymentGroupSwitch && !!isLessonPaymentGroupEnabled)
  const shouldShowSalaryFields =
    isSeminarian || !!teacherId || !!selectedTeacherId
  const teacherIdToSend = isSeminarian
    ? seminarianId
    : teacherId ?? selectedTeacherId

  const { additionalSettings, loadingAdditionalSettings } =
    useGetAdditionalSettings({ directionId: directionIdToSend })

  const applyMaxStudentsInDirection =
    !!additionalSettings?.max_students || additionalSettings?.max_students === 0

  useEffect(() => {
    if (isGroupTypeFixed || selectedDirectionHasLessonPaymentTariff) return

    form.setFieldsValue({
      is_lesson_payment_group: false,
    })
  }, [form, isGroupTypeFixed, selectedDirectionHasLessonPaymentTariff])

  useEffect(() => {
    if (applyMaxStudentsInDirection) {
      form.setFieldsValue({
        apply_max_students: applyMaxStudentsInDirection,
        max_students: additionalSettings?.max_students,
      })
      return
    }

    form.setFieldsValue({
      apply_max_students: applyMaxStudentsInDirection,
      max_students: null,
    })
  }, [additionalSettings?.max_students, applyMaxStudentsInDirection, form])

  const [randomName, setRandomName] = useState<groupName | null>(null)
  useEffect(() => {
    if (additionalSettings?.random_group_names_in_course) {
      const randomIndex = Math.floor(
        Math.random() * additionalSettings?.groups_names?.length,
      )
      setRandomName(additionalSettings?.groups_names[randomIndex])
      form.setFieldsValue({
        name: additionalSettings?.groups_names[randomIndex]?.name ?? '',
      })
      return
    }

    setRandomName(null)
    form.setFieldsValue({
      name: '',
    })
  }, [additionalSettings, form])

  useEffect(() => {
    if (shouldShowSalaryFields) return

    form.setFieldsValue({
      calculate_teacher_salary: false,
      salary_type: DEFAULT_SALARY_TYPE,
      salary: 0,
      duration_minutes: null,
      min_amount_enabled: false,
      min_amount: null,
    })
    form.validateFields(['salary', 'duration_minutes']).catch(() => null)
  }, [form, shouldShowSalaryFields])

  const getExpandForGetUser = useMemo(() => ['roles'], [])
  const salaryTypeOptions = useMemo(() => TEACHER_SALARY_OPTIONS, [])
  const salaryTypeTooltip = getTeacherSalaryTooltip(salaryType)
  const salaryLabel =
    salaryType === 'per-student-hour'
      ? 'Впишите зп за одного человека'
      : 'Впишите зарплату за одно занятие'
  const showDurationField = salaryType === 'per-duration'
  const showMinAmountFields = salaryType === 'per-student-hour'

  const {
    profile: profileSelectedEmployee,
    loading: loadingProfileSelectedEmployee,
  } = useGetUserProfile({
    userId: selectedTeacherId,
    expand: getExpandForGetUser,
    isGetSeminarianDirections: true,
  })

  const isIncludesRoleSeminarian: boolean =
    profileSelectedEmployee?.roles?.some(
      (el) => getBaseRole(el.item_name) === rolesEnum.seminarian,
    ) ?? false
  const isChosenCourseAvailableWithChosenTeacher: boolean =
    profileSelectedEmployee?.seminarianDirections?.some(
      (el) => el.direction_id === selectedDirectionId,
    ) ?? false

  const handlerSubmit: FormProps<FieldType>['onFinish'] = (values) => {
    setSubmitting(true)

    directionGroupApi
      .add({
        data: {
          direction_id: directionIdToSend,
          franchise_id: values.franchise_id,
          teacher_id: teacherIdToSend,
          name: values.name,
          max_students: values.apply_max_students ? values.max_students : null,
          additional_teacher_ids: values.has_additional_teachers
            ? values.additional_teacher_ids
            : [],
          suffix_type: additionalSettings?.random_group_names_in_course
            ? randomName?.suffix_type
            : null,
          ...(shouldShowSalaryFields
            ? {
                type: values.calculate_teacher_salary
                  ? values.salary_type
                  : DEFAULT_SALARY_TYPE,
                salary: values.calculate_teacher_salary
                  ? Number(values.salary)
                  : 0,
                ...(values.calculate_teacher_salary &&
                values.salary_type === 'per-duration'
                  ? {
                      duration_minutes: Number(values.duration_minutes),
                    }
                  : {}),
                ...(values.calculate_teacher_salary &&
                values.salary_type === 'per-student-hour'
                  ? {
                      duration_minutes: 60,
                      min_amount_enabled: !!values.min_amount_enabled,
                      min_amount: values.min_amount_enabled
                        ? Number(values.min_amount)
                        : null,
                    }
                  : {}),
              }
            : {}),
          ...(isLessonPaymentGroup
            ? {
                group_type: 'lesson-payment',
                lesson_duration: Number(values.lesson_duration),
                student_lesson_price: Number(values.student_lesson_price),
                is_shared_balance: !!values.is_shared_balance,
              }
            : {}),
        },
      })
      .then((res) => {
        const groupId = Number(res?.data?.data?.id)
        if (!groupId) throw new Error()

        onSuccess(groupId)
        addAlert({
          title: 'Успешно',
          text: `${groupNameSingular} успешно создана`,
          type: 'success',
        })
        onClose()
      })
      .catch(() => {
        addAlert({
          title: 'Ошибка',
          text: `Ошибка при создании ${groupNamePlural}`,
          type: 'error',
        })
        onClose()
      })
      .finally(() => {
        setSubmitting(false)
      })
  }

  return (
    <Form
      noValidate
      form={form}
      initialValues={{
        teacher_id: teacherId ?? null,
        franchise_id: defaultFranchiseId,
        name: '',
        direction_id: directionId,
        max_students: null,
        apply_max_students: false,
        calculate_teacher_salary: false,
        salary_type: DEFAULT_SALARY_TYPE,
        salary: 0,
        duration_minutes: null,
        lesson_duration: defaultLessonDuration ?? 60,
        student_lesson_price: studentLessonPrice ?? 0,
        is_lesson_payment_group: groupType === 'lesson-payment',
        is_shared_balance: false,
        min_amount_enabled: false,
        min_amount: null,
        has_additional_teachers: false,
        additional_teacher_ids: [],
      }}
      onFinish={handlerSubmit}
      className={cn(mclasses.modalContainer, classes.modalContainer)}
    >
      <div className={mclasses.modalHeader}>
        <div className={mclasses.modalTitleContainer}>
          <span className={mclasses.modalHeaderTitle}>
            Создание новой {groupNamePlural?.toLowerCase()} {isLessonPaymentGroup ? '(Оплата по занятиям)' : ''}
          </span>
        </div>
        <div className={mclasses.closeIcon} onClick={() => onClose()}>
          <IcoRemove />
        </div>
      </div>
      {franchisesLoading ? (
        <Preloader align="center" fullScreen="app" />
      ) : (
        <div
          className={cn(
            mclasses.formWrapper,
            mclasses.overflowAuto,
            classes.formWrapper,
          )}
        >
          <div>
            {franchisesEnabled && (
              <>
                <p className={mclasses.label}>Филиал</p>
                <FormItem
                  name="franchise_id"
                  rules={[{ required: true, message: 'Выберите филиал' }]}
                >
                  <Select
                    showSearch
                    className={cn(iclasses.newSelect, classes.selectField)}
                    popupClassName={iclasses.newSelectPopup}
                    placeholder="Выберите филиал"
                    options={availableFranchiseOptions}
                    loading={franchisesLoading}
                    onChange={() => {
                      if (!teacherId) {
                        form.setFieldValue('teacher_id', null)
                      }
                      if (enableCourseSelect) {
                        form.setFieldValue('direction_id', null)
                      }
                      form.setFieldValue('additional_teacher_ids', [])
                    }}
                    disabled={
                      availableFranchiseOptions.length === 1 &&
                      !!selectedFranchiseId
                    }
                    notFoundContent="Доступные филиалы не найдены"
                    filterOption={(input, option) =>
                      String(option?.label ?? '')
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  />
                </FormItem>
              </>
            )}
            {enableCourseSelect && (
              <>
                <p className={mclasses.label}>Курс</p>
                <FormItem
                  name="direction_id"
                  rules={[{ required: true, message: 'Выберите курс' }]}
                >
                  <Select
                    showSearch
                    className={cn(iclasses.newSelect, classes.selectField)}
                    popupClassName={iclasses.newSelectPopup}
                    placeholder="Выберите курс"
                    options={
                      franchisesEnabled && !selectedFranchiseId
                        ? []
                        : directionsState.loading
                          ? []
                          : directionsState.select
                    }
                    disabled={franchisesEnabled && !selectedFranchiseId}
                    notFoundContent={
                      selectedFranchiseId
                        ? 'В выбранном филиале нет подключенных курсов'
                        : 'Сначала выберите филиал'
                    }
                    loading={directionsState.loading}
                    filterOption={(input, option) =>
                      String(option?.label ?? '')
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  />
                </FormItem>
              </>
            )}

            <p className={mclasses.label}>
              Название {groupNamePlural?.toLowerCase()}
            </p>
            <FormItem
              name="name"
              rules={[{ required: true, message: 'Укажите название' }]}
              getValueProps={() => ({
                validate: form.getFieldError('name').length > 0,
              })}
            >
              <TooltipWrapper
                title={
                  additionalSettings?.random_group_names_in_course
                    ? 'Вы не можете изменить название, оно подставляется рандомно из возможных вариантов'
                    : ''
                }
              >
                <div
                  className={classNames({
                    [classes.wrapperInputName]:
                      additionalSettings?.random_group_names_in_course &&
                      randomName?.suffix_type === 'group_id',
                  })}
                >
                  {/*@ts-ignore*/}
                  <InputNew
                    className={classNames(iclasses.newInputDesign, {
                      [classes.inputName]:
                        additionalSettings?.random_group_names_in_course &&
                        randomName?.suffix_type === 'group_id',
                    })}
                    placeholder="Введите название"
                    errorText=" "
                    value={name}
                    onChange={(e: any) => {
                      form.setFieldsValue({
                        name: e?.target?.value,
                      })
                    }}
                    disabled={additionalSettings?.random_group_names_in_course}
                    typeValid="custom"
                  />
                  {additionalSettings?.random_group_names_in_course &&
                    randomName?.suffix_type === 'group_id' && (
                      <div className={classes.newGroupNameId}>: {`{id}`}</div>
                    )}
                </div>
              </TooltipWrapper>
            </FormItem>

            {!isSeminarian && !teacherId && (
              <>
                <p className={mclasses.label}>Педагог</p>
                <FormItem
                  name="teacher_id"
                  rules={[{ required: true, message: 'Выберите педагога' }]}
                >
                  <Select
                    showSearch
                    className={cn(iclasses.newSelect, classes.selectField)}
                    popupClassName={iclasses.newSelectPopup}
                    placeholder="Выберите педагога"
                    options={colleaguesState.loading ? [] : allColleagues}
                    loading={colleaguesState.loading}
                    disabled={franchisesEnabled && !selectedFranchiseId}
                    notFoundContent={
                      franchisesEnabled
                        ? 'В данной франшизе нет педагогов'
                        : 'Педагоги не найдены'
                    }
                    onSearch={(value) => setSearchName(value)}
                    onBlur={() => setSearchName('')}
                    filterOption={false}
                  />
                </FormItem>
                {loadingProfileSelectedEmployee ? (
                  <Preloader align="center" />
                ) : !isIncludesRoleSeminarian && !!selectedTeacherId ? (
                  <Text
                    className={classes.warningText}
                    texts={[
                      `У выбранного сотрудника нет роли педагога, она добавится ему автоматически при добавлении ему этой ${groupNamePlural?.toLowerCase()}`,
                    ]}
                  />
                ) : !isChosenCourseAvailableWithChosenTeacher &&
                  !!selectedTeacherId &&
                  !!selectedDirectionId ? (
                  <Text
                    className={classes.warningText}
                    texts={[
                      'У этого сотрудника нет доступа к этому курсу. При создании с ним группы система автоматически добавит ему доступ к курсу.',
                    ]}
                  />
                ) : null}
              </>
            )}

            <div className={classes.switchWrapper}>
              <div className={classes.switch}>
                <FormItem
                  name="has_additional_teachers"
                  valuePropName="checked"
                  noStyle
                >
                  <Switch
                    onChange={(checked) => {
                      if (!checked) {
                        form.setFieldValue('additional_teacher_ids', [])
                      }
                    }}
                  />
                </FormItem>
                <div>У данной группы есть дополнительные педагоги</div>
                <TooltipWrapper title={ADDITIONAL_TEACHERS_TOOLTIP}>
                  <InfoIcon />
                </TooltipWrapper>
              </div>
            </div>

            {hasAdditionalTeachers && (
              <>
                <p className={mclasses.label}>Дополнительные педагоги</p>
                <FormItem
                  name="additional_teacher_ids"
                  rules={[
                    {
                      required: true,
                      message: 'Выберите хотя бы одного дополнительного педагога',
                    },
                  ]}
                >
                  <BaseSelect
                    mode="multiple"
                    showSearch
                    className={classes.selectField}
                    placeholder="Выберите педагогов"
                    options={additionalTeacherOptions.filter(
                      (option: { value: number | string }) =>
                        Number(option.value) !== Number(teacherIdToSend),
                    )}
                    loading={additionalTeachersState.loading}
                    disabled={franchisesEnabled && !selectedFranchiseId}
                  />
                </FormItem>
              </>
            )}

            {shouldShowSalaryFields && (
              <>
                <div className={classes.switchWrapper}>
                  <FormItem
                    name="calculate_teacher_salary"
                    valuePropName="checked"
                  >
                    <div className={classes.switch}>
                      <Switch
                        checked={!!calculateTeacherSalary}
                        onChange={(checked) => {
                          form.setFieldsValue({
                            calculate_teacher_salary: checked,
                            salary_type: DEFAULT_SALARY_TYPE,
                            salary: checked ? (salaryValue ?? 0) : 0,
                            duration_minutes: null,
                            min_amount_enabled: false,
                            min_amount: null,
                          })
                        }}
                      />
                      <div>Рассчитывать зарплату педагога за проведенные занятия</div>
                    </div>
                  </FormItem>
                </div>
                {calculateTeacherSalary && (
                  <>
                    <p className={mclasses.label}>Тип зарплаты педагога</p>
                    <FormItem name="salary_type">
                      <Select
                        className={cn(iclasses.newSelect, classes.selectField)}
                        popupClassName={iclasses.newSelectPopup}
                        placeholder="Выберите тип зарплаты"
                        options={salaryTypeOptions}
                      />
                    </FormItem>
                    {salaryTypeTooltip && (
                      <Text
                        className={classes.warningText}
                        texts={[salaryTypeTooltip]}
                      />
                    )}
                    <FormItem
                      name="salary"
                      rules={[
                        {
                          validator: (_, value) => {
                            if (!calculateTeacherSalary) {
                              return Promise.resolve()
                            }

                            if (value === null || value === undefined) {
                              return Promise.reject(
                                new Error('Укажите зарплату'),
                              )
                            }

                            return Number(value) >= 0
                              ? Promise.resolve()
                              : Promise.reject(
                                  new Error('Укажите корректную зарплату'),
                                )
                          },
                        },
                      ]}
                    >
                      {/*@ts-ignore*/}
                      <InputWhite
                        onChange={(value: number | string) => {
                          const normalizedValue = normalizeNonNegativeNumberInput(
                            String(value ?? ''),
                          )
                          form.setFieldsValue({
                            salary:
                              normalizedValue === ''
                                ? null
                                : Number(normalizedValue),
                          })
                        }}
                        className={classes.inputWhite}
                        value={salaryValue}
                        placeholder="Введите значение"
                        type="number"
                        title={salaryLabel}
                        max="1000000"
                      />
                    </FormItem>
                  </>
                )}
                {calculateTeacherSalary && showDurationField && (
                  <FormItem
                    name="duration_minutes"
                    rules={[
                      {
                        validator: (_, value) => {
                          if (!calculateTeacherSalary) {
                            return Promise.resolve()
                          }

                          if (value === null || value === undefined) {
                            return Promise.reject(
                              new Error('Укажите длительность занятия'),
                            )
                          }

                          return Number(value) > 0
                            ? Promise.resolve()
                            : Promise.reject(
                                new Error(
                                  'Укажите корректную длительность занятия',
                                ),
                              )
                        },
                      },
                    ]}
                  >
                    {/*@ts-ignore*/}
                    <InputWhite
                      onChange={(value: number | string) => {
                        const normalizedValue = normalizeNonNegativeNumberInput(
                          String(value ?? ''),
                        )
                        form.setFieldsValue({
                          duration_minutes:
                            normalizedValue === ''
                              ? null
                              : Number(normalizedValue),
                        })
                      }}
                      className={classes.inputWhite}
                      value={durationMinutes}
                      placeholder="Введите длительность"
                      type="number"
                      title="Длительность занятия в минутах"
                    />
                  </FormItem>
                )}
                {calculateTeacherSalary && showMinAmountFields && (
                  <div className={classes.minAmountBlock}>
                    <FormItem name="min_amount_enabled" valuePropName="checked">
                      <Checkbox
                        checked={!!minAmountEnabled}
                        onChange={(event) => {
                          form.setFieldsValue({
                            min_amount_enabled: event.target.checked,
                          })
                        }}
                      >
                        <span className={classes.minAmountLabel}>
                          Выплачивать минимальную сумму, если не набирает человеко-часы
                          <TooltipWrapper title={MIN_AMOUNT_TOOLTIP}>
                            <span className={classes.infoIcon}>
                              <InfoIcon />
                            </span>
                          </TooltipWrapper>
                        </span>
                      </Checkbox>
                    </FormItem>
                    {minAmountEnabled && (
                      <FormItem
                        name="min_amount"
                        rules={[
                          {
                            validator: (_, value) => {
                              if (!shouldShowSalaryFields || !minAmountEnabled) {
                                return Promise.resolve()
                              }

                              if (value === null || value === undefined) {
                                return Promise.reject(
                                  new Error('Укажите минимальную сумму'),
                                )
                              }

                              return Number(value) >= 0
                                ? Promise.resolve()
                                : Promise.reject(
                                    new Error(
                                      'Укажите корректную минимальную сумму',
                                    ),
                                  )
                            },
                          },
                        ]}
                      >
                        {/*@ts-ignore*/}
                        <InputWhite
                          onChange={(value: number | string) => {
                            const normalizedValue = normalizeNonNegativeNumberInput(
                              String(value ?? ''),
                            )
                            form.setFieldsValue({
                              min_amount:
                                normalizedValue === ''
                                  ? null
                                  : Number(normalizedValue),
                            })
                          }}
                          className={classes.inputWhite}
                          value={minAmount}
                          placeholder="Введите сумму"
                          type="number"
                          title="Впишите минимальную сумму"
                        />
                      </FormItem>
                    )}
                  </div>
                )}
              </>
            )}

            {shouldShowLessonPaymentGroupSwitch && (
              <div className={classes.switchWrapper}>
                <FormItem name="is_lesson_payment_group" valuePropName="checked">
                  <div className={classes.switch}>
                    <Switch
                      checked={isLessonPaymentGroupEnabled || false}
                      onChange={(checked) => {
                        form.setFieldsValue({
                          is_lesson_payment_group: checked,
                        })
                      }}
                    />
                    <div>
                      Эта группа предназначена для тарифа с оплатой по занятию.
                    </div>
                  </div>
                </FormItem>
              </div>
            )}

            {isLessonPaymentGroup && (
              <>
                <div className={classes.twoColumns}>
                <FormItem
                  name="lesson_duration"
                  rules={[
                    {
                      validator: (_, value) => {
                        if (value === null || value === undefined) {
                          return Promise.reject(
                            new Error('Укажите стандартное время занятия'),
                          )
                        }

                        return Number(value) > 0
                          ? Promise.resolve()
                          : Promise.reject(
                              new Error(
                                'Укажите корректное стандартное время занятия',
                              ),
                            )
                      },
                    },
                  ]}
                >
                  {/*@ts-ignore*/}
                  <InputWhite
                    onChange={(value: number | string) => {
                      const normalizedValue = normalizeNonNegativeNumberInput(
                        String(value ?? ''),
                      )
                      form.setFieldsValue({
                        lesson_duration:
                          normalizedValue === ''
                            ? null
                            : Number(normalizedValue),
                      })
                    }}
                    className={classes.inputWhite}
                    value={lessonDuration}
                    placeholder="Введите время"
                    type="number"
                    title="Стандартное время занятия"
                  />
                </FormItem>
                <FormItem
                  name="student_lesson_price"
                  rules={[
                    {
                      validator: (_, value) => {
                        if (value === null || value === undefined) {
                          return Promise.reject(
                            new Error('Укажите стоимость урока'),
                          )
                        }

                        return Number(value) >= 0
                          ? Promise.resolve()
                          : Promise.reject(
                              new Error('Укажите корректную стоимость урока'),
                            )
                      },
                    },
                  ]}
                >
                  {/*@ts-ignore*/}
                  <InputWhite
                    onChange={(value: number | string) => {
                      const normalizedValue = normalizeNonNegativeNumberInput(
                        String(value ?? ''),
                      )
                      form.setFieldsValue({
                        student_lesson_price:
                          normalizedValue === ''
                            ? null
                            : Number(normalizedValue),
                      })
                    }}
                    className={classes.inputWhite}
                    value={studentLessonPriceValue}
                    placeholder="Введите стоимость"
                    type="number"
                    title="Стоимость урока (для ученика)"
                    max="1000000"
                  />
                </FormItem>
                </div>
                <div className={classes.switchWrapper}>
                  <FormItem name="is_shared_balance" valuePropName="checked">
                    <div className={classes.switch}>
                      <Checkbox
                        checked={isSharedBalance || false}
                        onChange={(event: any) => {
                          form.setFieldsValue({
                            is_shared_balance: event.target.checked,
                          })
                        }}
                      >
                        У данной группы один баланс на всех учащихся
                      </Checkbox>
                      <TooltipWrapper title="Если включить данный чекбокс, то у всех учащихся данной группы будет один общий баланс. Это позволит одному учащемуся (или родителю) платить сразу за всех">
                        <InfoIcon />
                      </TooltipWrapper>
                    </div>
                  </FormItem>
                </div>
              </>
            )}

            <div className={classes.switchWrapper}>
              <FormItem name="apply_max_students" valuePropName="checked">
                <div className={classes.switch}>
                  <TooltipWrapper
                    title={
                      applyMaxStudentsInDirection
                        ? 'Вы не можете изменить это значение, оно настроено для всех групп выбранного курса'
                        : ''
                    }
                  >
                    <Switch
                      checked={applyMaxStudents || false}
                      disabled={
                        applyMaxStudentsInDirection || loadingAdditionalSettings
                      }
                      onChange={(checked) => {
                        form.setFieldsValue({
                          apply_max_students: checked,
                        })
                      }}
                    />
                  </TooltipWrapper>
                  <div>Ограничить количество учащихся</div>
                  <TooltipWrapper
                    title={`Для ${groupNamePlural?.toLowerCase()} можно задать максимальное число учащихся. Когда это число будет достигнуто, система не будет позволять добавлять новых учащихся`}
                  >
                    <InfoIcon />
                  </TooltipWrapper>
                </div>
              </FormItem>
            </div>

            {applyMaxStudents && (
              <FormItem
                name="max_students"
                rules={[
                  {
                    required: true,
                    message: `Укажите максимальное количество учащихся для ${groupNamePlural?.toLowerCase()}`,
                  },
                ]}
              >
                <TooltipWrapper
                  title={
                    applyMaxStudentsInDirection
                      ? 'Вы не можете изменить это значение, оно настроено для всех групп выбранного курса'
                      : ''
                  }
                >
                  <div>
                    {/*@ts-ignore*/}
                    <InputWhite
                      onChange={(value: number | string) => {
                        form.setFieldsValue({
                          max_students: value,
                        })
                      }}
                      disabled={
                        applyMaxStudentsInDirection || loadingAdditionalSettings
                      }
                      className={classes.inputWhite}
                      value={maxStudents}
                      max="10000"
                      placeholder="Введите количество"
                      type="number"
                      title="Максимальное количество учащихся"
                    />
                  </div>
                </TooltipWrapper>
              </FormItem>
            )}
          </div>
        </div>
      )}
      <div className={mclasses.actions}>
        <Button color="gray" onClick={() => onClose()}>
          Отменить
        </Button>
        <Button
          type="submit"
          color="primary"
          disabled={
            submitting ||
            colleaguesState.loading ||
            directionsState.loading ||
            loadingAdditionalSettings
          }
          isLoading={submitting}
        >
          Подтвердить
        </Button>
      </div>
    </Form>
  )
}

const CreateGroupModal: FC<CreateGroupProps> = (props) => {
  const { open, onClose } = props

  return (
    <Modal
      active={open}
      className={classNames(mclasses.modalWrapper, classes.mwrapper) as any}
      setActive={() => onClose()}
    >
      <CreateGroupModalContent {...props} />
    </Modal>
  )
}

export default CreateGroupModal
